/*
 * Kaart met alle vestigingen, bedoeld als achtergrond van de showroom-hero.
 * De tekst valt over de kaart heen, dus de pins worden ingekaderd in het deel
 * dat vrij blijft: rechts van de tekstkolom op desktop, eronder op mobiel.
 */

// Zelfde breakpoint als de sectie-CSS, waar de tekst van naast naar boven de
// kaart schuift.
const MOBILE_BREAKPOINT = 749;
// Vanaf deze breedte zijn de pins aantikbaar. Op mobiel en tablet is de kaart te
// klein om een popup fijn te openen en weer weg te krijgen, dus daar is hij
// bewust puur beeld.
const POPUP_BREAKPOINT = 990;
const MAP_GUTTER = 32;
// Deel van de tekstkolom dat op desktop vrij van pins blijft. Lager = Nederland
// schuift verder naar links en de kaart vult meer; hoger = pins blijven strak
// rechts van de tekst.
const TEXT_CLEARANCE_RATIO = 0.7;
// Hoe lang we op leaflet.js wachten voordat we de kaart opgeven.
const LEAFLET_TIMEOUT = 10000;
// Publieke CARTO-key voor de basemap-tiles (voorkomt het "API KEY REQUIRED"-
// watermerk). Net als een Google Maps-key hoort deze in frontend-code thuis;
// beveiligd via domeinrestrictie in het CARTO-account, niet door geheimhouding.
const CARTO_API_KEY = 'cb1_2m12_1_b47c6b3a1734c01cc78280f1';

/* Vult een element met tekst, of haalt het weg als er geen waarde is. */
function fillOrRemove(element, value) {
  if (!element) return;

  const text = String(value ?? '').trim();
  if (text) {
    element.textContent = text;
  } else {
    element.remove();
  }
}

/*
 * Wacht tot leaflet.js zijn globale L heeft gezet, met een deadline erop: als
 * het script wegvalt of geblokkeerd wordt, blijft de lus anders eindeloos
 * doordraaien en houdt de openstaande promise de sectie in het geheugen.
 */
function waitForLeaflet() {
  if (window.L) return Promise.resolve(window.L);

  return new Promise((resolve) => {
    const deadline = performance.now() + LEAFLET_TIMEOUT;
    const check = () => {
      if (window.L) {
        resolve(window.L);
      } else if (performance.now() >= deadline) {
        resolve(null);
      } else {
        requestAnimationFrame(check);
      }
    };
    check();
  });
}

if (!customElements.get('showroom-map')) {
  class ShowroomMap extends HTMLElement {
    connectedCallback() {
      // De theme-editor haalt secties weg en hangt ze er weer in; na een
      // volledige opruiming moet de kaart dus opnieuw opgebouwd kunnen worden.
      if (this.map) return;
      this.init();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.refitFrame);
      clearTimeout(this.restoreTimer);
      if (this.handlePopupMediaChange) {
        this.popupMedia?.removeEventListener('change', this.handlePopupMediaChange);
      }
      this.restoreOverlay();
      this.map?.remove();
      this.map = null;
      this.markers = null;
      this.popupsEnabled = undefined;
    }

    async init() {
      const canvas = this.querySelector('[data-showroom-map-canvas]');
      const dataScript = this.querySelector('script[data-sample-request-store-data]');

      if (!canvas || !dataScript) return;

      let stores = [];
      try {
        stores = JSON.parse(dataScript.textContent) ?? [];
      } catch {
        return;
      }

      const located = stores.filter(
        (store) => typeof store.latitude === 'number' && typeof store.longitude === 'number'
      );

      if (!located.length) return;

      const L = await waitForLeaflet();

      // Tijdens het wachten kan de sectie al zijn weggehaald of opnieuw zijn
      // opgebouwd; in beide gevallen mag er hier geen kaart meer bij komen.
      if (!L || !this.isConnected || this.map) return;

      this.renderMap(L, canvas, located);
    }

    renderMap(L, canvas, stores) {
      this.canvas = canvas;
      this.bounds = stores.map((store) => [store.latitude, store.longitude]);

      // De kaart is een achtergrond, dus bewust niet te verslepen of te zoomen:
      // alle pins staan al in beeld en de pagina-scroll blijft ongemoeid.
      const map = L.map(canvas, {
        dragging: false,
        scrollWheelZoom: false,
        doubleClickZoom: false,
        touchZoom: false,
        boxZoom: false,
        keyboard: false,
        zoomControl: false,
        attributionControl: true,
      });

      // CARTO Positron: rustige, lichte basemap zodat de rode pins het beeld
      // bepalen i.p.v. wegen en plaatsnamen. {r} laadt retina-tiles waar nodig.
      L.tileLayer(`https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=${CARTO_API_KEY}`, {
        maxZoom: 18,
        subdomains: 'abcd',
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>-bijdragers &copy; <a href="https://carto.com/attributions" target="_blank" rel="noopener">CARTO</a>',
      }).addTo(map);

      this.moveOverlayIntoMap(L, map);

      const icon = L.divIcon({
        className: 'showroom-map__marker',
        html: '<span class="showroom-map__marker-pin"></span>',
        iconSize: [30, 30],
        iconAnchor: [15, 29],
        popupAnchor: [0, -26],
      });

      this.markers = stores.map((store) => ({
        store,
        marker: L.marker([store.latitude, store.longitude], {
          icon,
          title: store.name,
          alt: store.name,
        }).addTo(map),
      }));

      this.map = map;
      this.fitToClearArea();

      this.popupMedia = window.matchMedia(`(min-width: ${POPUP_BREAKPOINT}px)`);
      this.syncPopups();
      this.handlePopupMediaChange = () => this.syncPopups();
      this.popupMedia.addEventListener('change', this.handlePopupMediaChange);

      // Het openen van een popup schuift de kaart; zonder deze correctie blijft
      // die na het sluiten verschoven achter en valt de helft van het land
      // buiten beeld. Bij het wisselen van pin sluit Leaflet eerst de vorige
      // popup en opent daarna de nieuwe, beide in dezelfde tick: de timeout
      // loopt daar net achteraan en ziet dus dat er alweer een popup openstaat.
      map.on('popupopen', () => {
        this.popupOpen = true;
      });

      map.on('popupclose', () => {
        this.popupOpen = false;
        clearTimeout(this.restoreTimer);
        this.restoreTimer = setTimeout(() => {
          if (!this.popupOpen) this.fitToClearArea({ animate: true });
        }, 0);
      });

      // De hoogte van de tekstkolom (en dus het vrije deel van de kaart) hangt
      // af van de breedte, dus opnieuw inkaderen zodra de sectie van maat
      // verandert.
      this.resizeObserver = new ResizeObserver(() => {
        cancelAnimationFrame(this.refitFrame);
        this.refitFrame = requestAnimationFrame(() => {
          this.map?.invalidateSize({ animate: false });
          this.fitToClearArea();
          this.refreshAutoPanPadding();
        });
      });
      this.resizeObserver.observe(canvas);
    }

    /*
     * Hangt de popups aan de pins, of haalt ze er weer af onder de breakpoint.
     * Ook de tabindex gaat mee: een pin zonder popup hoort geen focuspunt te
     * zijn.
     */
    syncPopups() {
      const enabled = Boolean(this.popupMedia?.matches);
      if (!this.markers || enabled === this.popupsEnabled) return;
      this.popupsEnabled = enabled;

      // Met popups zit er bedienbare inhoud in de kaart, zonder is het puur een
      // plaatje.
      this.canvas?.setAttribute('role', enabled ? 'group' : 'img');

      const { paddingTopLeft, paddingBottomRight } = this.clearAreaPadding();

      for (const { marker, store } of this.markers) {
        if (enabled) {
          marker.bindPopup(this.popupContent(store), {
            closeButton: true,
            className: 'showroom-map__popup',
            maxWidth: 300,
            // Leaflet zet zijn kaartlagen in één stacking context, dus een popup
            // kan nooit boven de tekstlaag uitkomen. Daarom schuift de kaart de
            // popup het vrije gebied in, buiten de tekst.
            autoPanPaddingTopLeft: paddingTopLeft,
            autoPanPaddingBottomRight: paddingBottomRight,
          });
        } else {
          marker.closePopup();
          marker.unbindPopup();
        }

        const element = marker.getElement();
        if (!element) continue;

        if (enabled) {
          element.setAttribute('tabindex', '0');
        } else {
          element.removeAttribute('tabindex');
        }
      }
    }

    /*
     * De autopan-marges liggen vast zodra een popup gebonden is, terwijl de
     * tekstkolom bij elke resize van breedte verandert. Zonder bijwerken opent
     * een popup na het slepen van het venster alsnog onder de tekst.
     */
    refreshAutoPanPadding() {
      if (!this.markers) return;

      const { paddingTopLeft, paddingBottomRight } = this.clearAreaPadding();

      for (const { marker } of this.markers) {
        const popup = marker.getPopup();
        if (!popup) continue;

        popup.options.autoPanPaddingTopLeft = paddingTopLeft;
        popup.options.autoPanPaddingBottomRight = paddingBottomRight;
      }
    }

    get contentElement() {
      return this.closest('.showroom-map')?.querySelector('[data-showroom-map-content]') ?? null;
    }

    /*
     * Leaflet houdt tiles, pins en popups in één stacking context, dus een
     * gradient die náást de kaart staat ligt onvermijdelijk ook over de popups
     * heen. Als eigen laag tussen de tiles (z-index 200) en de pins (600) tint
     * hij alleen de kaart zelf. Gebeurt bewust in JS: zonder Leaflet blijft de
     * gradient staan waar hij in de sectie staat.
     */
    moveOverlayIntoMap(L, map) {
      const overlay = this.closest('.showroom-map')?.querySelector('.showroom-map__overlay');
      if (!overlay) return;

      // Onthouden waar hij vandaan komt: map.remove() sloopt de panes, dus bij
      // het afbreken moet de gradient eerst terug naar de sectie.
      this.overlay = overlay;
      this.overlayHome = { parent: overlay.parentElement, next: overlay.nextElementSibling };

      const pane = map.createPane('showroomOverlay');
      pane.style.zIndex = 450;
      pane.appendChild(overlay);
      overlay.classList.add('showroom-map__overlay--in-map');

      // De laag verschuift mee zodra de kaart pant, terwijl de gradient juist
      // stil moet staan ten opzichte van de tekst erover.
      const sync = () => {
        const size = map.getSize();
        overlay.style.width = `${size.x}px`;
        overlay.style.height = `${size.y}px`;
        L.DomUtil.setPosition(overlay, L.DomUtil.getPosition(map.getPane('mapPane')).multiplyBy(-1));
      };

      map.on('move zoom resize', sync);
      sync();
    }

    /* Zet de gradient terug op zijn plek in de sectie. */
    restoreOverlay() {
      if (!this.overlay || !this.overlayHome) return;

      this.overlay.classList.remove('showroom-map__overlay--in-map');
      this.overlay.removeAttribute('style');
      this.overlayHome.parent?.insertBefore(this.overlay, this.overlayHome.next);

      this.overlay = null;
      this.overlayHome = null;
    }

    /*
     * Ruimte die de tekst over de kaart inneemt, als padding voor fitBounds.
     * Gemeten op de tekstkolom zelf i.p.v. een vaste waarde, zodat het klopt
     * bij elke hoeveelheid tekst en elke schermbreedte.
     */
    clearAreaPadding() {
      const fallback = {
        paddingTopLeft: [MAP_GUTTER, MAP_GUTTER],
        paddingBottomRight: [MAP_GUTTER, MAP_GUTTER],
      };

      const content = this.contentElement;
      const panel = content?.querySelector('.group-block') ?? content;
      if (!panel || !this.canvas) return fallback;

      const panelRect = panel.getBoundingClientRect();
      const canvasRect = this.canvas.getBoundingClientRect();
      if (!panelRect.width || !canvasRect.width) return fallback;

      if (window.innerWidth <= MOBILE_BREAKPOINT) {
        const top = Math.max(MAP_GUTTER, panelRect.bottom - canvasRect.top + MAP_GUTTER);
        return {
          paddingTopLeft: [MAP_GUTTER, top],
          paddingBottomRight: [MAP_GUTTER, MAP_GUTTER],
        };
      }

      // Niet de volle tekstkolom vrijhouden, maar ~70% ervan: zo schuift
      // Nederland naar links en vult de kaart beter, terwijl de meeste pins nog
      // rechts van de tekst blijven (de rest valt onder het vervaagde deel van
      // de overlay).
      const column = panelRect.right - canvasRect.left;
      const left = Math.max(MAP_GUTTER, column * TEXT_CLEARANCE_RATIO + MAP_GUTTER);
      return {
        paddingTopLeft: [left, MAP_GUTTER],
        paddingBottomRight: [MAP_GUTTER, MAP_GUTTER],
      };
    }

    fitToClearArea({ animate = false } = {}) {
      if (!this.map || !this.bounds?.length) return;

      const { paddingTopLeft, paddingBottomRight } = this.clearAreaPadding();

      this.map.fitBounds(this.bounds, {
        paddingTopLeft,
        paddingBottomRight,
        maxZoom: 9,
        animate,
      });
    }

    /*
     * Popup-inhoud op basis van de template in de sectie. Ontbrekende gegevens
     * (geen foto, e-mail of vestigingspagina) laten hun blok gewoon weg.
     */
    popupContent(store) {
      const template = this.querySelector('template[data-showroom-map-popup]');
      if (!template) return null;

      const card = template.content.cloneNode(true).firstElementChild;
      if (!card) return null;

      const image = card.querySelector('[data-store-image]');
      if (store.imageUrl && image) {
        image.src = store.imageUrl;
      } else {
        card.querySelector('.showroom-map__card-hero')?.remove();
      }

      fillOrRemove(card.querySelector('[data-store-name]'), store.name);

      // Per onderdeel samenstellen, zodat een ontbrekende straat of plaats geen
      // "undefined" of losse komma in de kaart zet.
      const address = [store.street, [store.postalCode, store.city].filter(Boolean).join(' ')]
        .filter(Boolean)
        .join(', ');
      fillOrRemove(card.querySelector('[data-store-address]'), address);

      const phone = card.querySelector('[data-store-phone]');
      const phoneDigits = String(store.phone ?? '').replace(/[^0-9+]/g, '');
      if (phoneDigits && phone) {
        phone.href = `tel:${phoneDigits}`;
        fillOrRemove(card.querySelector('[data-store-phone-text]'), store.phone);
      } else {
        phone?.remove();
      }

      const email = card.querySelector('[data-store-email]');
      if (store.email && email) {
        email.href = `mailto:${store.email}`;
        fillOrRemove(card.querySelector('[data-store-email-text]'), store.email);
      } else {
        email?.remove();
      }

      const route = card.querySelector('[data-store-route]');
      if (route) {
        route.href = `https://www.google.com/maps/dir/?api=1&destination=${store.latitude},${store.longitude}`;
      }

      const page = card.querySelector('[data-store-page]');
      if (store.pageUrl && page) {
        page.href = store.pageUrl;
      } else {
        page?.remove();
      }

      return card;
    }
  }

  customElements.define('showroom-map', ShowroomMap);
}
